import { cn } from "@/lib/utils";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "ghostClean" | "outline";
export type ButtonSize = "sm" | "md" | "lg";

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "gradient-primary text-white font-bold shadow-[0_2px_8px_rgba(11,99,224,0.25)] active:shadow-[0_2px_8px_rgba(11,99,224,0.3)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-150 ease-out focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2",
  secondary:
    "bg-white text-primary border-2 border-primary/40 hover:bg-primary/8 hover:border-primary/60 hover:text-primary-dark active:shadow-[0_2px_6px_rgba(11,99,224,0.1)] hover:scale-[1.01] active:scale-[0.99] font-bold transition-all duration-150 ease-out focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 shadow-[0_1px_3px_rgba(11,99,224,0.1)]",
  ghost: "bg-transparent text-neutral-700 hover:text-neutral-900 border border-neutral-200 hover:border-neutral-400 hover:bg-neutral-100/80 active:shadow-none hover:scale-[1.01] active:scale-[0.99] font-semibold transition-all duration-150 ease-out focus-visible:ring-2 focus-visible:ring-neutral-300 focus-visible:ring-offset-2",
  ghostClean: "bg-transparent text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 border-0 rounded-full font-medium transition-colors active:scale-[0.99] focus-visible:ring-2 focus-visible:ring-neutral-300 focus-visible:ring-offset-2",
  outline:
    "bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50 hover:border-neutral-300 font-semibold transition-all duration-150 ease-out focus-visible:ring-2 focus-visible:ring-neutral-300 focus-visible:ring-offset-2",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-9 px-3 py-1.5 text-xs sm:h-10 sm:px-4 sm:py-2 sm:text-sm font-semibold",
  md: "h-10 px-4 py-2 text-sm sm:h-12 sm:px-6 sm:py-3 sm:text-base font-bold",
  lg: "h-11 px-5 py-2.5 text-sm sm:h-12 sm:px-6 sm:py-3 sm:text-base md:h-14 md:px-8 md:py-4 md:text-lg font-bold",
};

type ButtonClassesOptions = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
};

export function buttonClasses({
  variant = "primary",
  size = "md",
  fullWidth,
  className,
}: ButtonClassesOptions = {}) {
  return cn(
    // Buttons are the one deliberate pill in the app. See the radius scale in globals.css.
    "relative inline-flex items-center justify-center gap-2.5 rounded-full font-bold transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-dark disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100 min-w-fit",
    variantClasses[variant],
    sizeClasses[size],
    fullWidth && "w-full",
    className,
    // Ensure primary buttons always have white text, even if className overrides it
    variant === "primary" && "!text-white",
  );
}


